import { useState, useCallback, useEffect } from 'react';
import { usePaginationState } from './usePaginationState';
import type { MultiSelectOption } from 'src/components/common/MultiSelect';
import type { CalculatedBreakdownType } from '@erp/shared-types';
import type { DateRange } from 'react-day-picker';

export interface ExportFilters {
  dateRange: DateRange | undefined;
  agreements: MultiSelectOption[];
  agreementTags: MultiSelectOption[];
  supplierTags: MultiSelectOption[];
  productGroupTags: MultiSelectOption[];
  suppliers: MultiSelectOption[]; // Direct supplier selection
  divisions: MultiSelectOption[];
  type?: CalculatedBreakdownType | null; // Only for calculated breakdowns
}

interface UseExportTableOptions {
  initialPageSize?: number;
  defaultType?: CalculatedBreakdownType | null;
  onFiltersChange?: (filters: ExportFilters) => void;
}

interface UseExportTableReturn {
  // Pagination
  pagination: ReturnType<typeof usePaginationState>;

  // Filters
  filters: ExportFilters;
  setDateRange: (dateRange: DateRange | undefined) => void;
  setAgreements: (agreements: MultiSelectOption[]) => void;
  setAgreementTags: (tags: MultiSelectOption[]) => void;
  setSupplierTags: (tags: MultiSelectOption[]) => void;
  setProductGroupTags: (tags: MultiSelectOption[]) => void;
  setSuppliers: (suppliers: MultiSelectOption[]) => void;
  setDivisions: (divisions: MultiSelectOption[]) => void;
  setType: (type: CalculatedBreakdownType | null) => void;

  // Computed
  buildApiFilters: () => Record<string, unknown>;

  // Reset
  resetFilters: () => void;
}

/**
 * Get default date range: Jan 1 - Dec 31 of current year
 * Uses local timezone for date picker display, but will be converted to UTC when sent to API
 */
function getDefaultDateRange(): DateRange {
  const now = new Date();
  const currentYear = now.getFullYear();
  // Create dates in local timezone (midnight local time) for proper date picker display
  const startDate = new Date(currentYear, 0, 1); // Jan 1 at local midnight
  const endDate = new Date(currentYear, 11, 31); // Dec 31 at local midnight
  return { from: startDate, to: endDate };
}

/**
 * Convert a Date to UTC date string (YYYY-MM-DD)
 */
function toUTCDateString(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Shared hook for export table components.
 * Manages pagination state and common filter state.
 */
export function useExportTable(
  options: UseExportTableOptions = {},
): UseExportTableReturn {
  const {
    initialPageSize = 100,
    defaultType = null,
    onFiltersChange,
  } = options;

  // Pagination state
  const pagination = usePaginationState({
    initialPageSize,
  });

  // Filter state
  const [filters, setFilters] = useState<ExportFilters>({
    dateRange: getDefaultDateRange(),
    agreements: [],
    agreementTags: [],
    supplierTags: [],
    productGroupTags: [],
    suppliers: [],
    divisions: [],
    type: defaultType,
  });

  // Update pagination info when filters change (reset to page 1)
  useEffect(
    () => {
      pagination.goToFirstPage();
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.dateRange?.from,
      filters.dateRange?.to,
      filters.agreements,
      filters.agreementTags,
      filters.supplierTags,
      filters.productGroupTags,
      filters.suppliers,
      filters.divisions,
      filters.type,
    ],
  );

  // Notify parent of filter changes
  useEffect(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  const setDateRange = useCallback((dateRange: DateRange | undefined) => {
    setFilters((prev) => ({ ...prev, dateRange, agreements: [] }));
  }, []);

  const setAgreements = useCallback((agreements: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, agreements }));
  }, []);

  const setAgreementTags = useCallback((tags: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, agreementTags: tags }));
  }, []);

  const setSupplierTags = useCallback((tags: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, supplierTags: tags }));
  }, []);

  const setProductGroupTags = useCallback((tags: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, productGroupTags: tags }));
  }, []);

  const setSuppliers = useCallback((suppliers: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, suppliers }));
  }, []);

  const setDivisions = useCallback((divisions: MultiSelectOption[]) => {
    setFilters((prev) => ({ ...prev, divisions }));
  }, []);

  const setType = useCallback((type: CalculatedBreakdownType | null) => {
    setFilters((prev) => ({ ...prev, type }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      dateRange: getDefaultDateRange(),
      agreements: [],
      agreementTags: [],
      supplierTags: [],
      productGroupTags: [],
      suppliers: [],
      divisions: [],
      type: null,
    });
    pagination.reset();
  }, [pagination]);

  // Build API filter object
  const buildApiFilters = useCallback(
    () => {
      const apiFilters: Record<string, unknown> = {
        first: pagination.pageSize,
        offset: pagination.getCurrentOffset(),
        sortBy: pagination.sortBy,
        sortDirection: pagination.sortDirection,
      };

      // Add date range filters (convert to UTC date strings)
      if (filters.dateRange?.from) {
        apiFilters.startDate = toUTCDateString(filters.dateRange.from);
      }
      if (filters.dateRange?.to) {
        apiFilters.endDate = toUTCDateString(filters.dateRange.to);
      }

      if (filters.agreements.length > 0) {
        apiFilters.agreementIds = filters.agreements.map((a) => a.id).join(',');
      }

      if (filters.agreementTags.length > 0) {
        apiFilters.agreementTagIds = filters.agreementTags
          .map((t) => t.id)
          .join(',');
      }

      if (filters.supplierTags.length > 0) {
        apiFilters.supplierTagIds = filters.supplierTags
          .map((t) => t.id)
          .join(',');
      }

      if (filters.productGroupTags.length > 0) {
        apiFilters.productGroupTagIds = filters.productGroupTags
          .map((t) => t.id)
          .join(',');
      }

      if (filters.suppliers.length > 0) {
        apiFilters.supplierIds = filters.suppliers.map((s) => s.id).join(',');
      }

      if (filters.divisions.length > 0) {
        apiFilters.divisionIds = filters.divisions.map((d) => d.id).join(',');
      }

      if (filters.type) {
        apiFilters.type = filters.type;
      }

      return apiFilters;
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination.getCurrentOffset is confusing the linter
    [
      filters,
      pagination.getCurrentOffset,
      pagination.pageSize,
      pagination.sortBy,
      pagination.sortDirection,
    ],
  );

  return {
    pagination,
    filters,
    setDateRange,
    setAgreements,
    setAgreementTags,
    setSupplierTags,
    setProductGroupTags,
    setSuppliers,
    setDivisions,
    setType,
    buildApiFilters,
    resetFilters,
  };
}
